import type { Distance, Percentage, Point } from "@pdfking/math"
import { Center } from "@pdfking/math"
import type { Matrix, PageShape } from "@pdfking/renderer-api"
import { GroupMatrix, MatrixCompose, MatrixIdentity, PlotFunction } from "@pdfking/renderer-api"

// The mirror across the vertical line through `x`.
const mirror = (x: Distance): Matrix<Distance> =>
  GroupMatrix({ origin: { x, y: 0 as Distance }, scale: { x: -1, y: 1 } as Point<Percentage> })

// What faces a direction is mirrored back about its own centre: under the
// sheet's mirror the two cancel into a move, so it lands mirrored and reads the
// right way round. Its turns, and a group's, come out the other way — a mirror,
// a turn and a mirror again is the turn reversed.
const unmirror = (shape: PageShape): PageShape => {
  switch (shape.enum) {
    case PlotFunction.PLOT_GROUP:
      return { ...shape, shapes: shape.shapes.map(unmirror) }
    case PlotFunction.PLOT_TEXT:
      return shape.position
        ? {
            enum: PlotFunction.PLOT_GROUP,
            matrix: mirror(Center(shape.position).x),
            shapes: [shape],
          }
        : shape
    // The page flips back under its own matrix; the box and its mask stay
    // under the sheet's mirror, so the bleed swaps sides with the box.
    case PlotFunction.PLOT_MEDIA:
      return shape.position
        ? {
            ...shape,
            media_matrix: MatrixCompose(
              mirror(Center(shape.position).x),
              shape.media_matrix ?? MatrixIdentity()
            ),
          }
        : shape
    default:
      return shape
  }
}

/**
 * Shapes seen in a mirror standing at `center` across x, as one group carrying
 * the mirror. Not the ink mirrored: a sheet turned over does not mirror ink —
 * its back is another page — so text and pages are each mirrored back.
 */
export const MirrorShapes = (shapes: PageShape[], center: Distance): PageShape[] =>
  shapes.length
    ? [{ enum: PlotFunction.PLOT_GROUP, matrix: mirror(center), shapes: shapes.map(unmirror) }]
    : shapes
